import { useState, useMemo, useEffect } from "react";
import { Search, Plus, Minus, Trash2, Loader2, Printer, RotateCcw, Banknote, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useProducts, useCheckoutSettings, createStoreOrder, fetchOrderOnce } from "@/lib/firestoreService";
import { formatPrice } from "@/lib/cart";
import { printStoreReceipt } from "@/lib/printReceipt";
import { track } from "@/lib/analytics";
import { orderNumber } from "@/lib/orderNumber";

export const StoreBilling = () => {
  const { profile } = useAuth();
  const products = useProducts();
  const settings = useCheckoutSettings();
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products.filter((p) => p.in_stock !== false);
    return products.filter(
      (p) => p.in_stock !== false && (
        (p.name || "").toLowerCase().includes(q) ||
        (p.name_te || "").toLowerCase().includes(q)
      )
    );
  }, [products, search]);

  const billItems = lines.map((l) => ({
    product_id: l.product_id,
    size: l.size,
    quantity: l.quantity,
  }));

  const subtotal = lines.reduce((s, l) => s + l.price * l.quantity, 0);
  const gstPercent = settings.gstPercent ?? 5;
  const gstAmount = Math.round(subtotal * gstPercent / 100);
  const grandTotal = subtotal + gstAmount;

  const paymentOptions = [
    { key: "cash", icon: Banknote, label: "Cash" },
    ...(settings.enableStoreUpi !== false ? [{ key: "upi", icon: Smartphone, label: "UPI Transfer" }] : []),
  ];

  // If an admin turns UPI off mid-bill while it's the selected method,
  // fall back to Cash rather than let the sale complete with a payment
  // method that's no longer an option on screen.
  useEffect(() => {
    if (paymentMethod === "upi" && settings.enableStoreUpi === false) {
      setPaymentMethod("cash");
    }
  }, [settings.enableStoreUpi, paymentMethod]);

  const addProduct = (product, size) => {
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.product_id === product.id && l.size === size.label);
      if (idx >= 0) {
        return prev.map((l, i) => i === idx ? { ...l, quantity: l.quantity + 1 } : l);
      }
      return [...prev, {
        key: `${product.id}-${size.label}`,
        product_id: product.id,
        name: product.name,
        size: size.label,
        price: size.price,
        quantity: 1,
      }];
    });
    toast.success(`Added ${product.name} (${size.label})`);
  };

  const updateQty = (key, qty) => {
    setLines((prev) =>
      qty <= 0 ? prev.filter((l) => l.key !== key) : prev.map((l) => l.key === key ? { ...l, quantity: qty } : l)
    );
  };

  const resetBill = () => {
    setLines([]);
    setCustomerName("");
    setPhone("");
    setPaymentMethod("cash");
  };

  const completeSale = async () => {
    if (lines.length === 0) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      const { id, order_number } = await createStoreOrder({
        items: billItems,
        customer_name: customerName.trim() || "Walk-in Customer",
        phone: phone.trim() || null,
        payment_method: paymentMethod,
      });
      track("store_sale_completed", {
        order_id: id,
        order_number,
        item_count: lines.reduce((s, l) => s + l.quantity, 0),
        unique_product_count: lines.length,
        product_names: lines.map(l => l.name).join(", "),
        order_total: grandTotal,
        payment_method: paymentMethod,
        // Store-sale customers never sign in, so there's no Mixpanel People
        // profile to attach this to (unlike the website, where identify()
        // covers signed-in customers) — capture it directly on the event.
        customer_name: customerName.trim() || null,
        customer_phone: phone.trim() || null,
      });
      toast.success(`Sale complete — ${orderNumber({ id, order_number })}`);
      const order = await fetchOrderOnce(id);
      if (order) printStoreReceipt(order);
      resetBill();
    } catch (e) {
      toast.error(e.message || "Could not save bill");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="store-billing-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#5A2D1D]">Store Billing</h2>
          <p className="text-sm text-[#2E1A12]/60">
            {profile?.name ? `Welcome, ${profile.name}` : "Walk-in home orders"} — {settings.enableStoreUpi !== false ? "Cash or UPI only" : "Cash only"} (no cards)
          </p>
        </div>
        <button type="button" onClick={resetBill} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5A2D1D] border border-[#5A2D1D]/30 px-3 py-2 rounded-sm hover:bg-[#5A2D1D]/5">
          <RotateCcw className="w-4 h-4" /> New bill
        </button>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-4">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A2D1D]/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search pickles…" className="w-full pl-9 pr-3 py-2.5 rounded-sm bg-white/60 border border-[#5A2D1D]/25 text-sm focus:outline-none focus:border-[#5A2D1D]" />
          </div>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
            {filtered.map((p) => (
              <div key={p.id} className="bg-[#EFD9B4]/50 border border-[#5A2D1D]/15 rounded-sm p-3">
                <div className="font-display font-bold text-[#5A2D1D] text-sm">{p.name}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(p.sizes || []).map((sz) => (
                    <button key={sz.label} type="button" onClick={() => addProduct(p, sz)} className="text-xs font-bold px-2.5 py-1.5 rounded-sm bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC]">
                      {sz.label} · {formatPrice(sz.price)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-5">
          <h3 className="font-display text-lg font-bold text-[#5A2D1D] mb-4">Current Bill</h3>
          {lines.length === 0 ? (
            <p className="text-sm text-[#2E1A12]/50 py-8 text-center">Tap a size to add items</p>
          ) : (
            <ul className="divide-y divide-[#5A2D1D]/10 mb-4">
              {lines.map((l) => (
                <li key={l.key} className="py-2.5 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#5A2D1D] text-sm truncate">{l.name}</div>
                    <div className="text-xs text-[#556B2F] font-bold">{l.size} · {formatPrice(l.price)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => updateQty(l.key, l.quantity - 1)} className="w-7 h-7 grid place-items-center border border-[#5A2D1D]/25 rounded-sm"><Minus className="w-3 h-3" /></button>
                    <span className="w-6 text-center text-sm font-bold">{l.quantity}</span>
                    <button type="button" onClick={() => updateQty(l.key, l.quantity + 1)} className="w-7 h-7 grid place-items-center border border-[#5A2D1D]/25 rounded-sm"><Plus className="w-3 h-3" /></button>
                  </div>
                  <div className="font-bold text-sm w-16 text-right">{formatPrice(l.price * l.quantity)}</div>
                  <button type="button" onClick={() => updateQty(l.key, 0)} className="text-[#7A3B25]/60 hover:text-[#7A3B25]"><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}

          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name (optional)" className="px-3 py-2 rounded-sm border border-[#5A2D1D]/25 text-sm bg-white/50" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" type="tel" className="px-3 py-2 rounded-sm border border-[#5A2D1D]/25 text-sm bg-white/50" />
          </div>

          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-2">Payment (Cash / UPI only)</div>
            <div className="flex gap-2">
              {paymentOptions.map((opt) => (
                <button key={opt.key} type="button" onClick={() => setPaymentMethod(opt.key)} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-sm border-2 font-bold text-sm ${paymentMethod === opt.key ? "border-[#556B2F] bg-[#556B2F]/10 text-[#556B2F]" : "border-[#5A2D1D]/20 text-[#2E1A12]/70"}`}>
                  <opt.icon className="w-4 h-4" /> {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-[#5A2D1D]/15 pt-3 space-y-1 text-sm mb-4">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between"><span>GST ({gstPercent}%)</span><span>{formatPrice(gstAmount)}</span></div>
            <div className="flex justify-between font-display text-xl font-bold text-[#5A2D1D] pt-2 border-t border-[#5A2D1D]/15"><span>Total</span><span>{formatPrice(grandTotal)}</span></div>
          </div>

          <button type="button" onClick={completeSale} disabled={saving || lines.length === 0} className="w-full py-4 rounded-sm bg-[#556B2F] hover:bg-[#6a8438] disabled:opacity-40 text-[#F5E6CC] font-bold text-lg inline-flex items-center justify-center gap-2">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Printer className="w-5 h-5" />}
            Complete Sale & Print
          </button>
        </div>
      </div>
    </div>
  );
};
